import { useEffect, useState } from 'react';

import { ApiError } from '@eneco/dc/client/ApiError';
import { Label } from '@eneco/dc/client/types';
import { basketMachine } from '@eneco/flows/machines/basketMachine';
import { Customers_Address_AddressModel, DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';
import Cookies from 'js-cookie';

import { energyTypeIncludes } from '../helpers';
import { energyChoiceTypeMachine } from '../machines/energyChoice/energyChoice.machine';
import { EnergyType } from '../types';

interface Props {
  label: Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  addressFinderData?: Customers_Address_AddressModel;
  isHouseNumberSuffixRequired?: boolean;
  addressFinderErrorData?: ApiError;
}

export function useCalculationLifecycle({
  label,
  businessUnit,
  addressFinderData,
  isHouseNumberSuffixRequired,
  addressFinderErrorData,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: Props): any {
  const [cookieId, setCookieId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'failed-unsupported' | 'failed-unavailable' | 'failed-api-error'
  >('idle');
  const [energyTypeBasedOnConnections, setEnergyTypeBasedOnConnections] = useState<EnergyType>(
    EnergyType.ElectricityAndGas,
  );

  const [basketState, sendToBasketMachine] = useMachine(basketMachine);
  const [energyChoiceState, sendToEnergyChoiceMachine] = useMachine(energyChoiceTypeMachine, {
    input: { businessUnit, label },
  });

  const {
    context: { availableProducts = [] },
  } = energyChoiceState;

  useEffect(() => {
    if (!basketState.context?.basketId) {
      sendToBasketMachine({
        type: 'CREATE_BASKET',
        config: { businessUnit, label },
      });
    }

    if (basketState.context?.basketId) {
      const flowName = label === 'oxxio' ? 'oxxio_energy_flow_basket' : 'energy-flow_basket';
      Cookies.set(flowName, basketState.context.basketId);
      setCookieId(basketState.context.basketId);
    }
  }, [basketState.context.basketId, businessUnit, label, sendToBasketMachine]);

  useEffect(() => {
    if (addressFinderErrorData) return;
    if (!energyChoiceState.matches('IDLE')) {
      setIsLoading(energyChoiceState.matches('FETCH'));

      if (energyChoiceState.matches('SUCCESS')) {
        const hasUnsupported = availableProducts.some(
          (type: EnergyType) =>
            energyTypeIncludes(type, EnergyType.Warmth) || energyTypeIncludes(type, EnergyType.GeothermalHeating),
        );

        const hasValid = availableProducts.some(
          (type: EnergyType) =>
            energyTypeIncludes(type, EnergyType.ElectricityAndGas) || energyTypeIncludes(type, EnergyType.Electricity),
        );

        if (availableProducts.includes(EnergyType.ElectricityAndGas)) {
          setEnergyTypeBasedOnConnections(EnergyType.ElectricityAndGas);
        } else if (availableProducts.some((type: EnergyType) => energyTypeIncludes(type, EnergyType.Gas))) {
          setEnergyTypeBasedOnConnections(EnergyType.ElectricityAndGas);
        } else if (availableProducts.some((type: EnergyType) => energyTypeIncludes(type, EnergyType.Electricity))) {
          setEnergyTypeBasedOnConnections(EnergyType.Electricity);
        }

        if (hasUnsupported) {
          setStatus('failed-unsupported');
        } else if (!hasValid) {
          setStatus('failed-unavailable');
        } else {
          setStatus('success');
        }
      }
      if (energyChoiceState.matches('ERROR')) {
        setStatus('failed-api-error');
      }
    }
  }, [energyChoiceState, availableProducts]);

  useEffect(() => {
    const houseNumberSuffix =
      addressFinderData?.houseNumberSuffix !== '-' ? addressFinderData?.houseNumberSuffix?.toString() : '';

    const isAddressComplete =
      !!addressFinderData?.postalCode &&
      !!addressFinderData?.houseNumber &&
      (isHouseNumberSuffixRequired ? !!houseNumberSuffix : true);

    if (isAddressComplete) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        type: 'FETCH_ENERGY_TYPES',
        values: {
          postalCode: addressFinderData?.postalCode?.toString(),
          houseNumber: Number(addressFinderData.houseNumber),
          houseNumberSuffix,
        },
      };
      sendToEnergyChoiceMachine(payload);
    }
  }, [
    addressFinderData?.postalCode,
    addressFinderData?.houseNumber,
    addressFinderData?.houseNumberSuffix,
    isHouseNumberSuffixRequired,
    sendToEnergyChoiceMachine,
  ]);

  useEffect(() => {
    if (addressFinderErrorData) {
      sendToEnergyChoiceMachine({
        type: 'RESET_ENERGY_TYPES',
      });
    }
  }, [addressFinderErrorData]);

  // 2. Reset state when postal code changes
  useEffect(() => {
    setIsLoading(false);
    setStatus('idle');
  }, [addressFinderData?.postalCode]);

  return {
    isLoading,
    status,
    cookieId,
    energyTypeBasedOnConnections,
    energyChoiceState,
    sendToEnergyChoiceMachine,
    basketState,
    sendToBasketMachine,
  };
}
